import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AppSidebar, { SB_W, SB_W_COLLAPSED } from "../../../AppSidebar";
import { PageToolbarSearch, FilterTriggerButton, ChoiceFilterPopover } from "../../common/components";
import { listFilterTemplates } from "../../common/services/filterTemplatesApi";
import { epicsApi } from "../api/epicsApi";
import EpicDrawer from "../components/EpicDrawer";
import "../styles/EpicsViewV2Page.css";
import "../../../AppSidebar.css";
import "../../../MasterViewV2.css";
import "../../../V2SideDrawer.css";

const EPIC_STATUS_OPTIONS = [
  { value: "Planning",            label: "Planning" },
  { value: "In-Progress",         label: "In Progress" },
  { value: "Regression Testing",  label: "Regression Testing" },
  { value: "UAT Released",        label: "UAT Released" },
  { value: "Staging Released",    label: "Staging Released" },
  { value: "Live",                label: "Live" },
  { value: "Closed",              label: "Closed" },
];

const RECURRENCE_OPTIONS = [
  { value: "Onetime", label: "One-time" },
  { value: "Weekly",  label: "Weekly" },
  { value: "Monthly", label: "Monthly" },
];

const DEFAULT_EPIC_FILTERS = {
  status: { op: "is_not", values: ["Closed"] },
};

function buildApiParams(filters, searchText) {
  const params = {};
  if (searchText.trim()) params.search = searchText.trim();

  const statusFilter = filters?.status;
  if (statusFilter?.values?.length > 0) {
    if (statusFilter.op === "is") params.status_in = statusFilter.values.join(",");
    else if (statusFilter.op === "is_not") params.status_not_in = statusFilter.values.join(",");
  }

  const customerFilter = filters?.customer_name;
  if (customerFilter?.values?.length > 0) {
    if (customerFilter.op === "is") params.customer_name_in = customerFilter.values.join(",");
    else if (customerFilter.op === "is_not") params.customer_name_not_in = customerFilter.values.join(",");
  }

  const recurrenceFilter = filters?.recurrence;
  if (recurrenceFilter?.values?.length > 0) {
    if (recurrenceFilter.op === "is") params.recurrence_in = recurrenceFilter.values.join(",");
    else if (recurrenceFilter.op === "is_not") params.recurrence_not_in = recurrenceFilter.values.join(",");
  }

  return params;
}

function formatDate(value) {
  if (!value) return "—";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function formatRange(startDate, endDate) {
  return `${formatDate(startDate)} → ${formatDate(endDate)}`;
}

function epicStatusClass(status) {
  const s = String(status || "").trim().toLowerCase();
  if (s === "planning") return "mv2-stpill st-open";
  if (s === "in-progress") return "mv2-stpill st-inprogress";
  if (s === "regression testing" || s === "uat released" || s === "staging released") {
    return "mv2-stpill st-onhold";
  }
  if (s === "live" || s === "closed") return "mv2-stpill st-completed";
  return "mv2-stpill st-na";
}

function toSelectProjectRows(projectsResp) {
  const rows = Array.isArray(projectsResp)
    ? projectsResp
    : Array.isArray(projectsResp?.projects)
      ? projectsResp.projects
      : [];
  return rows.map((p) => ({
    id: p.id,
    project_name: p.project_name || "—",
    customer_name: p.customer_name || "",
    customer_id: p.customer_id || "",
  }));
}

function canManageEpics(user) {
  const role = String(user?.db_role || "").trim().toLowerCase();
  const userType = String(user?.db_user_type || "").trim().toLowerCase();
  return (role === "admin" || role === "super admin" || role === "am") && (userType === "internal" || userType === "");
}

function isAdmin(user) {
  const role = String(user?.db_role || "").trim().toLowerCase();
  const userType = String(user?.db_user_type || "").trim().toLowerCase();
  return (role === "admin" || role === "super admin") && (userType === "internal" || userType === "");
}

function todayFirstOfMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function todayLastOfMonth() {
  const d = new Date();
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, "0")}-${String(last.getDate()).padStart(2, "0")}`;
}

export default function EpicsViewV2Page({ user, showToast }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("sb-collapsed") === "true"
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [epics, setEpics] = useState([]);
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_EPIC_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    listFilterTemplates("epics").then((templates) => {
      const def = (templates || []).find((t) => t.is_default);
      if (def) setAppliedFilters(def.filter_def || {});
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [drawer, setDrawer] = useState({ open: false, mode: "new", epic: null });
  const [saving, setSaving] = useState(false);
  const canEdit = useMemo(() => canManageEpics(user), [user]);
  const userIsAdmin = useMemo(() => isAdmin(user), [user]);
  const searchDebounceRef = useRef(null);

  const EMPTY_GEN_FORM = { epic_name: "", from_date: todayFirstOfMonth(), to_date: todayLastOfMonth(), project_ids: [], planned_hours: "" };
  const [genModal, setGenModal] = useState({ open: false });
  const [genForm, setGenForm] = useState(EMPTY_GEN_FORM);
  const [genLoading, setGenLoading] = useState(false);
  const [genResult, setGenResult] = useState(null);
  const [overrideIds, setOverrideIds] = useState(new Set());

  const customerOptions = useMemo(() => {
    const seen = new Set();
    return projects
      .filter((p) => p.customer_name && !seen.has(p.customer_name) && seen.add(p.customer_name))
      .map((p) => ({ value: p.customer_name, label: p.customer_name }));
  }, [projects]);

  const filterFields = useMemo(() => [
    { key: "status",        label: "Status",     options: EPIC_STATUS_OPTIONS },
    { key: "customer_name", label: "Customer",   options: customerOptions },
    { key: "recurrence",    label: "Recurrence", options: RECURRENCE_OPTIONS },
  ], [customerOptions]);

  const activeFilterCount = useMemo(
    () => Object.values(appliedFilters).filter((v) => v?.values?.length > 0).length,
    [appliedFilters],
  );

  const loadEpics = useCallback(async (params) => {
    setLoading(true);
    setError("");
    try {
      const resp = await epicsApi.list(params);
      // Keep list load O(1): do not fan-out per epic row.
      const enriched = (Array.isArray(resp) ? resp : []).map((epic) => ({
        ...epic,
        release_plan_summary: epic?.release_plan_summary || "—",
        progress: epic?.progress || { label: "—", pct: 0 },
      }));
      setEpics(enriched);
    } catch (err) {
      setError(String(err?.message || "Failed to load epics"));
      setEpics([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProjects = useCallback(async () => {
    try {
      const resp = await epicsApi.listProjects();
      setProjects(toSelectProjectRows(resp));
    } catch {
      // non-critical — drawer project picker will be empty
    }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      loadEpics(buildApiParams(appliedFilters, search));
    }, 300);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [search, appliedFilters, loadEpics]);

  const openCreate = () => setDrawer({ open: true, mode: "new", epic: null });
  const openEdit = (epic) => setDrawer({ open: true, mode: "edit", epic });
  const closeDrawer = () => setDrawer({ open: false, mode: "new", epic: null });

  const handleSave = async (payload) => {
    if (!canEdit) return;
    setSaving(true);
    try {
      if (drawer.mode === "edit" && drawer.epic?.id) {
        const result = await epicsApi.update(drawer.epic.id, payload);
        const tasksMoved = Number(result?.tasks_moved) || 0;
        showToast?.(tasksMoved > 0 ? `Epic moved — ${tasksMoved} task${tasksMoved === 1 ? "" : "s"} updated to match.` : "Epic updated");
      } else {
        await epicsApi.create(payload);
        showToast?.("Epic created");
      }
      closeDrawer();
      await loadEpics(buildApiParams(appliedFilters, search));
    } catch (err) {
      showToast?.(err?.message || "Failed to save epic", "error");
    } finally {
      setSaving(false);
    }
  };

  const openGenModal = () => {
    setGenForm(EMPTY_GEN_FORM);
    setGenResult(null);
    setOverrideIds(new Set());
    setGenModal({ open: true });
  };
  const closeGenModal = () => {
    setGenModal({ open: false });
    if (genResult?.created?.length > 0) loadEpics(buildApiParams(appliedFilters, search));
  };

  const buildBody = (extraOverrides = []) => {
    const body = {
      epic_name: genForm.epic_name.trim(),
      from_date: genForm.from_date,
      to_date: genForm.to_date,
    };
    if (genForm.project_ids.length > 0) body.project_ids = genForm.project_ids;
    if (genForm.planned_hours !== "") body.planned_hours = Number(genForm.planned_hours);
    if (extraOverrides.length > 0) body.override_project_ids = extraOverrides;
    return body;
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!genForm.epic_name.trim() || !genForm.from_date || !genForm.to_date) return;
    setGenLoading(true);
    setGenResult(null);
    setOverrideIds(new Set());
    try {
      const result = await epicsApi.bulkCreateSupportEpics(buildBody());
      setGenResult(result);
    } catch (err) {
      showToast?.(err?.message || "Failed to create support epics", "error");
    } finally {
      setGenLoading(false);
    }
  };

  const handleOverride = async () => {
    if (overrideIds.size === 0) return;
    const ids = [...overrideIds];
    setGenLoading(true);
    try {
      const body = {
        epic_name: genForm.epic_name.trim(),
        from_date: genForm.from_date,
        to_date: genForm.to_date,
        project_ids: ids,
        override_project_ids: ids,
      };
      if (genForm.planned_hours !== "") body.planned_hours = Number(genForm.planned_hours);
      const result = await epicsApi.bulkCreateSupportEpics(body);
      setGenResult((prev) => ({
        created: [...(prev?.created || []), ...(result.created || [])],
        warnings: [
          ...(prev?.warnings || []).filter((w) => !overrideIds.has(w.project_id)),
          ...(result.warnings || []),
        ],
      }));
      setOverrideIds(new Set());
    } catch (err) {
      showToast?.(err?.message || "Failed to create overridden epics", "error");
    } finally {
      setGenLoading(false);
    }
  };

  return (
    <div className="epv2-root" style={{ gridTemplateColumns: sidebarCollapsed ? `${SB_W_COLLAPSED} 1fr` : `${SB_W} 1fr` }}>
      <AppSidebar
        user={user}
        collapsed={sidebarCollapsed}
        onCollapse={() => setSidebarCollapsed((c) => {
          const next = !c;
          localStorage.setItem("sb-collapsed", String(next));
          return next;
        })}
      />

      <div className="epv2-main">
        <div className="page-head">
          <h1>
            Epics <em>{epics.length}</em>
          </h1>
          <div className="page-head-actions">
            {userIsAdmin ? (
              <button type="button" className="epv2-gen-occ-btn v2-btn-secondary" onClick={openGenModal}>
                Create Support Epics
              </button>
            ) : null}
            {canEdit ? (
              <button type="button" className="epv2-new-btn v2-btn-primary" onClick={openCreate}>
                <span className="plus">+</span>
                New Epic
              </button>
            ) : null}
          </div>
        </div>

        <div className="page-toolbar">
          <div className="page-toolbar-right epv2-toolbar-right">
            <PageToolbarSearch
              value={search}
              onChange={setSearch}
              placeholder="Search epics..."
            />
            <FilterTriggerButton
              activeCount={activeFilterCount}
              onClick={() => setFilterOpen(true)}
              onClear={() => setAppliedFilters({})}
            />
          </div>
        </div>

        <ChoiceFilterPopover
          open={filterOpen}
          title="Filter Epics"
          fields={filterFields}
          appliedFilters={appliedFilters}
          module="epics"
          onApply={(draft) => setAppliedFilters(draft)}
          onClose={() => setFilterOpen(false)}
        />

        <div className="page-table-wrap epv2-table-wrap">
          <table className="page-table epv2-table">
            <thead>
              <tr>
                <th>Epic Code</th>
                <th>Epic Name</th>
                <th>Project</th>
                <th>Status</th>
                <th>Frequency</th>
                <th className="num">Est Hours</th>
                <th>Start / End</th>
                <th>Progress</th>
                <th>Created By</th>
                <th>Created At</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={10} className="epv2-state">Loading epics...</td>
                </tr>
              )}
              {!loading && error && (
                <tr>
                  <td colSpan={10} className="epv2-state epv2-state--error">{error}</td>
                </tr>
              )}
              {!loading && !error && epics.length === 0 && (
                <tr>
                  <td colSpan={10} className="epv2-state">No epics found.</td>
                </tr>
              )}
              {!loading && !error && epics.map((epic) => (
                <tr key={epic.id} className="epv2-row" onClick={() => openEdit(epic)}>
                  <td className="epv2-code">{epic.epic_code || "—"}</td>
                  <td>{epic.epic_name || "—"}</td>
                  <td>{epic.project_name || "—"}</td>
                  <td>
                    <span className={epicStatusClass(epic.status)}>{epic.status || "—"}</span>
                  </td>
                  <td>{epic.recurrence_frequency || "—"}</td>
                  <td className="num">{Number(epic.estimated_hours || 0).toLocaleString("en-IN")}</td>
                  <td>{formatRange(epic.start_date, epic.end_date)}</td>
                  <td>
                    <div className="epv2-progress">
                      <div className="epv2-progress-bar">
                        <span style={{ width: `${epic.progress?.pct || 0}%` }} />
                      </div>
                      <span>{epic.progress?.label || "—"}</span>
                    </div>
                  </td>
                  <td>{epic.created_by_name || "—"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{epic.created_at ? formatDate(epic.created_at) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <EpicDrawer
        open={drawer.open}
        mode={drawer.mode}
        epic={drawer.epic}
        projects={projects}
        readOnly={!canEdit}
        saving={saving}
        onClose={closeDrawer}
        onSave={handleSave}
        showToast={showToast}
      />

      {genModal.open && (
        <div className="epv2-gen-modal-overlay" onClick={closeGenModal}>
          <div className="epv2-gen-modal" onClick={(e) => e.stopPropagation()}>
            <div className="epv2-gen-modal-head">
              <h2>Create Support Epics</h2>
              <button type="button" className="epv2-gen-modal-close" onClick={closeGenModal}>✕</button>
            </div>

            {!genResult && (
              <form className="epv2-gen-modal-form" onSubmit={handleGenerate}>
                <div className="epv2-gen-field">
                  <label>Epic Name</label>
                  <input
                    type="text"
                    value={genForm.epic_name}
                    onChange={(e) => setGenForm((f) => ({ ...f, epic_name: e.target.value }))}
                    placeholder="e.g. June 2026 Support"
                    required
                  />
                </div>
                <div className="epv2-gen-row-2">
                  <div className="epv2-gen-field">
                    <label>From Date</label>
                    <input
                      type="date"
                      value={genForm.from_date}
                      onChange={(e) => setGenForm((f) => ({ ...f, from_date: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="epv2-gen-field">
                    <label>To Date</label>
                    <input
                      type="date"
                      value={genForm.to_date}
                      onChange={(e) => setGenForm((f) => ({ ...f, to_date: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                <div className="epv2-gen-field">
                  <label>Planned Hours per Occurrence <span className="epv2-gen-optional">(optional — defaults to 0)</span></label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={genForm.planned_hours}
                    onChange={(e) => setGenForm((f) => ({ ...f, planned_hours: e.target.value }))}
                    placeholder="e.g. 40"
                  />
                </div>
                <div className="epv2-gen-field">
                  <label>Projects <span className="epv2-gen-optional">(optional — leave blank for all active projects)</span></label>
                  <div className="epv2-gen-project-list">
                    {projects.map((p) => {
                      const checked = genForm.project_ids.includes(p.id);
                      return (
                        <label key={p.id} className="epv2-gen-project-item">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() =>
                              setGenForm((f) => ({
                                ...f,
                                project_ids: checked
                                  ? f.project_ids.filter((id) => id !== p.id)
                                  : [...f.project_ids, p.id],
                              }))
                            }
                          />
                          <span className="epv2-gen-project-name">{p.project_name}</span>
                          {p.customer_name ? <span className="epv2-gen-project-customer">{p.customer_name}</span> : null}
                        </label>
                      );
                    })}
                    {projects.length === 0 && <span className="epv2-gen-empty">No projects loaded</span>}
                  </div>
                </div>

                <div className="epv2-gen-modal-actions">
                  <button type="button" className="v2-btn-secondary" onClick={closeGenModal} disabled={genLoading}>
                    Cancel
                  </button>
                  <button type="submit" className="v2-btn-primary" disabled={genLoading || !genForm.epic_name.trim()}>
                    {genLoading ? "Creating…" : "Create"}
                  </button>
                </div>
              </form>
            )}

            {genResult && (
              <div className="epv2-gen-result">
                {/* Created */}
                {genResult.created.length > 0 && (
                  <div className="epv2-gen-section">
                    <div className="epv2-gen-section-head epv2-gen-section-head--ok">
                      {genResult.created.length} epic{genResult.created.length !== 1 ? "s" : ""} created
                      {genResult.created.some((c) => c.overridden) ? " (includes overrides)" : ""}
                    </div>
                    <table className="epv2-gen-result-table">
                      <thead>
                        <tr>
                          <th>Customer</th>
                          <th>Project</th>
                          <th className="num">Occurrences</th>
                        </tr>
                      </thead>
                      <tbody>
                        {genResult.created.map((c) => (
                          <tr key={c.epic_id}>
                            <td>{c.customer_name}</td>
                            <td>{c.project_name}{c.overridden ? <span className="epv2-gen-override-tag">override</span> : null}</td>
                            <td className="num">{c.occurrences_created}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Warnings */}
                {genResult.warnings.length > 0 && (
                  <div className="epv2-gen-section">
                    <div className="epv2-gen-section-head epv2-gen-section-head--warn">
                      {genResult.warnings.length} project{genResult.warnings.length !== 1 ? "s" : ""} skipped — overlapping recurring epic exists
                    </div>
                    <p className="epv2-gen-warn-hint">Check the projects you want to create anyway, then click Override.</p>
                    <table className="epv2-gen-result-table">
                      <thead>
                        <tr>
                          <th className="epv2-gen-col-check"></th>
                          <th>Customer</th>
                          <th>Project</th>
                          <th>Conflicting Epics</th>
                        </tr>
                      </thead>
                      <tbody>
                        {genResult.warnings.map((w) => {
                          const checked = overrideIds.has(w.project_id);
                          return (
                            <tr key={w.project_id} className={checked ? "epv2-gen-row--override-sel" : ""}>
                              <td className="epv2-gen-col-check">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() =>
                                    setOverrideIds((prev) => {
                                      const next = new Set(prev);
                                      if (checked) next.delete(w.project_id);
                                      else next.add(w.project_id);
                                      return next;
                                    })
                                  }
                                />
                              </td>
                              <td>{w.customer_name}</td>
                              <td>{w.project_name}</td>
                              <td className="epv2-gen-conflicts">{w.conflicting_epics.join(", ")}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div className="epv2-gen-override-actions">
                      <button
                        type="button"
                        className="v2-btn-secondary"
                        disabled={overrideIds.size === 0 || genLoading}
                        onClick={() => setOverrideIds(new Set(genResult.warnings.map((w) => w.project_id)))}
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        className="v2-btn-primary"
                        disabled={overrideIds.size === 0 || genLoading}
                        onClick={handleOverride}
                      >
                        {genLoading ? "Creating…" : `Override Selected (${overrideIds.size})`}
                      </button>
                    </div>
                  </div>
                )}

                {genResult.created.length === 0 && genResult.warnings.length === 0 && (
                  <p className="epv2-gen-empty" style={{ padding: "16px 24px" }}>No projects matched the selected scope.</p>
                )}

                <div className="epv2-gen-modal-actions" style={{ padding: "0 24px 20px" }}>
                  <button type="button" className="v2-btn-secondary" onClick={closeGenModal}>
                    {genResult.warnings.length === 0 ? "Done" : "Close"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
